import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {UseHealthDistrictSelectionProps} from './types';
import {useGetHealthDistrictsQuery} from '../../queries/useGetHealthDistrictsQuery';
import {TypeLocalNameEnum} from 'domain/usecases';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {APIErrorsMessage, useMediaQueries, useQueryParams} from 'presentation/pages';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';
import {useEffect, useState} from 'react';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {textHandle} from 'presentation/utils/textHandle';
import {parseCommaSeparated} from 'presentation/modules/booking/shared/utils/parseCommaSeparated';
import {translations} from 'presentation/translations-new';
import {useRequestSchedulePermission} from 'presentation/modules/booking/shared/hooks/useRequestSchedulePermission';

const {all, selectAll} = translations['pt-br'].commons;

export function useHealthDistrictSelection({handleNextStep}: UseHealthDistrictSelectionProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const bookingDistricts = parseCommaSeparated(bookingData?.districtName);
	const bookingCities = parseCommaSeparated(bookingData?.citiesNames);
	const districtsInitialState = bookingDistricts.map((d, i) => `${d}-${bookingCities[i] ?? ''}`);
	const [selectedDistricts, setSelectedDistricts] = useState<string[]>(districtsInitialState);
	const [showEmptyDistrictsModal, setShowEmptyDistrictsModal] = useState(false);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const {isEnabled: pacmanFlagIsEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_PACMAN);
	const {isMobile} = useMediaQueries();
	const {
		SEARCH_PARAMS: {query},
	} = useQueryParams();

	const districtParams: GetHealthDistrictsParams = {
		stateCode: bookingData.ufCode?.toUpperCase(),
		locationName: bookingData.cityName?.toUpperCase(),
		locationType: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(bookingData.specialtyCode),
	};

	const {
		refetch,
		isError: districtListHasError,
		isFetching: districtListIsLoading,
		data: districtList,
		error: districtListError,
	} = useGetHealthDistrictsQuery({params: districtParams, enabled: true});

	const {
		handleGetHealthRequestSchedulePermission,
		healthRequestSchedulePermissionState,
		requestSchedulePermissionIsDone,
	} = useRequestSchedulePermission();

	const districtListHasUnprocessableError = isUnprocessableError(districtListError);

	const specialtyNotAvailableOnline =
		districtListHasUnprocessableError &&
		districtListError?.message === APIErrorsMessage.NOT_ALLOWED_FOR_SPECIALTY;

	const shouldFetchPacman = districtListHasUnprocessableError && pacmanFlagIsEnabled;

	const isLoading =
		districtListIsLoading || (shouldFetchPacman && !requestSchedulePermissionIsDone);

	const showNotFoundState =
		!isLoading &&
		(!districtList ||
			districtList.length === 0 ||
			(districtListHasError && districtListHasUnprocessableError));

	const filteredDistrictList =
		districtList?.filter(districtItem =>
			textHandle(districtItem.district)
				.sanitizeText()
				.includes(textHandle(query).sanitizeText()),
		) ?? [];

	const showEmptyState = filteredDistrictList?.length === 0;

	const allowsRequestScheduleRegistration = Boolean(
		healthRequestSchedulePermissionState?.allowsRegistration && pacmanFlagIsEnabled,
	);

	const handleGoForward = () => {
		if (selectedDistricts.length === 0) {
			setShowEmptyDistrictsModal(true);
			return;
		}
		handleNextStep();
	};

	const handleSelectCard = (district: string, skipScroll?: boolean) => {
		if (district === `${all}-${selectAll}`) {
			const newAllSelected = !bookingData.allDistrictsSelected;
			const districts = districtList?.map(d => d.district);
			const cities = districtList?.map(d => d.city);

			setBookingData(prev => ({
				...prev,
				districtName: newAllSelected ? districts?.join(', ') : '',
				citiesNames: newAllSelected ? cities?.join(', ') : '',
				allDistrictsSelected: newAllSelected,
			}));

			setSelectedDistricts(
				newAllSelected ? (districtList?.map(d => `${d.district}-${d.city}`) ?? []) : [],
			);

			const shouldScrollToForwardButton =
				!isMobile && newAllSelected && districts && districts.length >= 12 && !skipScroll;

			if (shouldScrollToForwardButton) {
				document.getElementById('go-forward-button')?.scrollIntoView({behavior: 'smooth'});
			}
			return;
		}

		setSelectedDistricts(prevSelected => {
			const updatedSelected: string[] = prevSelected.includes(district)
				? prevSelected.filter(item => item !== district)
				: [...prevSelected, district];

			const districts = updatedSelected?.map(item => item?.split('-')[0]);
			const cities = updatedSelected?.map(item => item?.split('-')[1]);

			setBookingData(prev => ({
				...prev,
				districtName: districts.join(', '),
				citiesNames: cities.join(', '),
				allDistrictsSelected: updatedSelected.length === districtList?.length,
			}));

			return updatedSelected;
		});
	};

	useEffect(() => {
		if (shouldFetchPacman) {
			handleGetHealthRequestSchedulePermission({
				city: bookingData.cityName?.toUpperCase(),
				specialtyCode: bookingData.specialtyCode,
				typeLocalName: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
				stateCode: bookingData.ufCode?.toUpperCase(),
			});
		}
	}, [districtListHasUnprocessableError]);

	if (showNotFoundState) {
		setTimeout(() => {
			setHideSearchBarState(showNotFoundState);
		}, 0);
	}

	return {
		showNotFoundState,
		districtListHasError,
		districtListHasUnprocessableError,
		refetch,
		districtListIsLoading,
		showEmptyState,
		filteredDistrictList,
		selectedDistricts,
		showEmptyDistrictsModal,
		setShowEmptyDistrictsModal,
		handleSelectCard,
		handleGoForward,
		specialtyNotAvailableOnline,
		isLoading,
		healthRequestSchedulePermissionState,
		allowsRequestScheduleRegistration,
	};
}
